/**
 * =============================================================================
 * Project: lajt-online.pl
 * Created Date: 2017-08-03, 11:19:25
 * Author: Michał Rejman <michal.rejman@telestrada.pl>
 * =============================================================================
 * Last Modified: 2018-03-01, 12:54:14
 * Modified By: Sebastian Drzewicki
 * =============================================================================
 * Copyright (c) 2017 Telestrada
 * =============================================================================
 */
import Vue from 'vue';
import store from '@/store';
import { mapGetters, mapActions } from 'vuex';
import sDate from '@/services/sDate';

// COMPONENTS //////////////////////////////////////////////////////////////////
import PanelContent from '@/components/Panel/PanelContent';
import PanelFooter from '@/components/Panel/PanelFooter';
import TopMenu from '@/components/Panel/TopMenu';
import BottomMenu from '@/components/Panel/BottomMenu';
import RodoTerms from '@/components/Common/RodoTerms';
import CentralModalFrame from '@/components/Help/CentralModalFrame';
import ServiceChangeConfirmation from '@/components/Modals/ServiceChangeConfirmation';
import BannerPopup from '@/components/Modals/BannerPopup';

// CONTAINER ///////////////////////////////////////////////////////////////////
const Panel = {
  components: {
    PanelContent,
    PanelFooter,
    TopMenu,
    BottomMenu,
    RodoTerms,
    CentralModalFrame,
    ServiceChangeConfirmation,
    BannerPopup,
  },
  data() {
    return {
      modalInUse: '',
      service: null,
      popupBanner: null,
      windowWidth: 0,
    };
  },
  store,
  computed: {
    ...mapGetters(['subscriber', 'bannersList', 'brandLogos', 'panelFooterIcons', 'shouldShowRodoTerms', 'acl', 'activationFailed',
      'pendingStoreActions', 'hasServiceStatusBeenChange', 'confirmationRequired', 'enabled', 'incorrect_code',
    ]),
    // eslint-disable-next-line consistent-return
    prepareProperProps() {
      if (this.modalInUse === 'ServiceChangeConfirmation') {
        return {
          service: this.service,
          number: this.subscriber.number,
          hasStatusBeenChange: this.hasServiceStatusBeenChange,
          confirmationRequired: this.confirmationRequired,
          enabled: this.enabled,
          incorrect_code: this.incorrect_code,
          activationFailed: this.activationFailed,
        };
      }
      if (this.modalInUse === 'BannerPopup') {
        return { banner: this.popupBanner };
      }
    },
    // eslint-disable-next-line consistent-return
    currentComponent() {
      if (this.modalInUse === 'ServiceChangeConfirmation') {
        return ServiceChangeConfirmation;
      }
      if (this.modalInUse === 'BannerPopup') {
        return BannerPopup;
      }
    },
  },
  watch: {
    bannersList(banners) {
      const filtered = banners.filter(x => x.show_popup);
      if (!filtered.length) return;
      this.$EventBus.$emit('Banner/TOGGLE_MODAL_WITH_POPUP', filtered[0]);
    },
  },
  methods: {
    ...mapActions(['getMe',
      'setRechargesTerms', 'setBillingTerms', 'getRechargesList', 'getBillingList',
      'getBannerList', 'showMobileMenuList', 'hideMobileMenuList', 'getInformationAboutMainComponentsVisibility',
      'getPanelFooterIcons', 'getFaqList', 'getCustomerPaymentsData', 'getCustomerInvoicesData',
      'getRodoTerms', 'changeStatus', 'getServicesList', 'clearChangeStatusResult', 'confirmStatusChange',
    ]),
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    __callDedicatedForPostpaidVuexActions() {
      if (this.acl.privileges.can_see_payments) {
        this.getCustomerPaymentsData();
        this.getCustomerInvoicesData();
      }
    },
    __callDedicatedForPrepaidVuexActions() {
      const yesterday = sDate.formatDate((d => new Date(d.setDate(d.getDate() - 1)))(new Date()));
      const currentMonthStart = sDate.getDateByDay('firstDay', 'currentMonth');
      this.setRechargesTerms({ dateStart: currentMonthStart, dateEnd: yesterday })
        .then(this.getRechargesList);
    },
    async __callDedicatedForBothAccountTypeVuexActions() {
      const yesterday = sDate.formatDate((d => new Date(d.setDate(d.getDate() - 1)))(new Date()));
      const currentMonthStart = sDate.getDateByDay('firstDay', 'currentMonth');
      await this.getMe();
      this.setBillingTerms({ dateStart: currentMonthStart, dateEnd: yesterday })
        .then(this.getBillingList);
      this.getBannerList();
      this.getInformationAboutMainComponentsVisibility();
      this.getPanelFooterIcons();
      this.getFaqList();
    },
    async handleChangeStatus(uuid, currentStatus) {
      const params = {
        uuid,
        action: currentStatus ? 'disable' : 'enable',
      };
      await this.changeStatus(params);
    },
    async handleConfirmStatusChange(uuid, currentStatus, smsCode) {
      const params = {
        uuid,
        action: currentStatus ? 'disable' : 'enable',
        sms_code: smsCode,
      };
      await this.changeStatus(params);
    },
    closeModal() {
      this.modalInUse = '';
      this.service = null;
    },
  },
  async beforeMount() {
    await this.__callDedicatedForBothAccountTypeVuexActions();

    if (this.subscriber.type === 'prepaid') {
      this.__callDedicatedForPrepaidVuexActions();
    }
    if (this.subscriber.type === 'postpaid') {
      this.__callDedicatedForPostpaidVuexActions();
    }
  },
  beforeDestroy() {
    this.$EventBus.$off('Services/CLOSE_MODAL');
    this.$EventBus.$off('ServiceChangeConfirmation/CHANGE_STATUS');
    this.$EventBus.$off('ServiceChangeConfirmation/CLOSE_MODAL');
    this.$EventBus.$off('Service/TOGGLE_MODAL_WITH_STATUS_CHANGE');
    this.$EventBus.$off('ServiceChangedConfirmation/CANCEL_CONFIRMATION');
    this.$EventBus.$off('Banner/TOGGLE_MODAL_WITH_POPUP');
    this.$EventBus.$off('Banner/CLOSE_MODAL_WITH_POPUP');
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
      this.onResize();
    });
    this.$EventBus.$on('ServiceChangeConfirmation/CHANGE_STATUS', (service, status) => {
      this.handleChangeStatus(service, status).then();
    });
    this.$EventBus.$on('ServiceChangeConfirmation/CLOSE_MODAL', () => {
      this.closeModal();
      this.$store.commit('TOGGLE_SERVICE_STATUS_CHANGE_RESULT', false);
    });
    this.$EventBus.$on('ServiceChangedConfirmation/CANCEL_CONFIRMATION', () => {
      this.closeModal();
    });
    this.$EventBus.$on('ServiceChangedConfirmation/SUBMIT_CONFIRMATION', (service, status, smsCode) => {
      this.handleConfirmStatusChange(service, status, smsCode);
    });
    this.$EventBus.$on('Services/CLOSE_MODAL', () => {
      this.closeModal();
    });
    this.$EventBus.$on('Service/TOGGLE_MODAL_WITH_STATUS_CHANGE', (service) => {
      this.service = service;
      this.modalInUse = 'ServiceChangeConfirmation';
    });
    this.$EventBus.$on('Banner/TOGGLE_MODAL_WITH_POPUP', (banner) => {
      this.popupBanner = banner;
      this.modalInUse = 'BannerPopup';
    });
    this.$EventBus.$on('Banner/CLOSE_MODAL_WITH_POPUP', () => {
      this.closeModal();
    });
  },
  template: `
  <div>
      <CentralModalFrame v-if="modalInUse!==''" :onclose="closeModal">
          <template slot="modal">
              <component :is="currentComponent" v-bind="prepareProperProps"/>
          </template>
      </CentralModalFrame>
      <div class="my-container">
          <RodoTerms/>
          <v-touch @swipeleft="hideMobileMenuList" @swiperight="showMobileMenuList" v-bind:swipe-options="{ direction: 'horizontal' }">
              <TopMenu :subscriber="subscriber" :brand-logos="brandLogos"/>
              <PanelContent/>
              <PanelFooter :subscriber="subscriber" :footer-icons="panelFooterIcons" :windowWidth="windowWidth"/>
              <BottomMenu :subscriber="subscriber"/>
          </v-touch>
      </div>
  </div>
`,
};

export default Vue
  .component('Panel', Panel);



// WEBPACK FOOTER //
// ./src/containers/Panel/index.js